/**
 * 챌린지를 **어디서 가져오는가** — 이 판정의 단일 지점.
 *
 * 두 곳이 있다.
 *   · QBase (웹)      — 브라우저가 QBase(:8445)를 직접 읽는다. 지금까지의 동작.
 *   · 로컬 (내부망)    — 파일로 가져온 문제를 LiveSQL 백엔드(SQLite)가 준다.
 *
 * 내부망에는 QBase 가 없어 챌린지가 통째로 사라졌다. 파일로 옮긴 문제를 로컬에 두면
 * **채점은 그대로 돈다** — 채점이 브라우저에서 일어나기 때문이다(정답 SQL 을 실행해
 * 사용자 SQL 결과와 비교). 옮길 로직은 없고 공급원만 바뀐다.
 *
 * ## 자동으로 정하지 않는다 (사용자 결정 2026-09-19)
 *
 * 처음에는 "가져온 문제가 있으면 로컬" 로 자동 전환했다. 그런데 웹에서 샘플을 한 번 넣으면
 * QBase 챌린지가 통째로 사라진 것처럼 보였고, **왜 목록이 바뀌었는지 화면에 아무 단서가
 * 없었다.** 조용히 바뀌는 것이 문제다 — 지금은 사용자가 고르고, 고른 값이 화면에 보인다.
 *
 *   · QBase 를 쓸 수 없으면(스탠드얼론) 고를 것이 없다 — 언제나 로컬이다.
 *   · 쓸 수 있으면 기본은 QBase 고, 고른 값을 기억한다.
 */
import axios from 'axios';
import { IS_STANDALONE } from '../config/features';

const API = process.env.REACT_APP_API_BASE_URL || '/sql-developer';
/** QBase 를 쓸 수 있는가 — 스탠드얼론은 테넌트 id 가 비어 있어 애초에 못 쓴다. */
const QBASE_AVAILABLE = !IS_STANDALONE && !!process.env.REACT_APP_QBASE_SYS_ID;

export const QBASE = 'qbase';
export const LOCAL = 'local';

/** 로컬 챌린지 API 의 기준 경로. */
export const LOCAL_API = `${API}/challenge`;

/** 지금 보는 공급원. 스탠드얼론은 고를 것이 없어 언제나 로컬이다. */
/**
 * 출처는 **환경이 정한다**(사용자 결정 2026-10-06): 웹 = 클라우드(QBase), 스탠드얼론 = 가져온 것(로컬 저장소).
 * 예전에는 웹에서도 사용자가 「가져온 것」 을 고를 수 있었는데, 웹은 클라우드 저장이 우선이고 가져오기도 클라우드로 간다 —
 * 출처가 둘이면 같은 챌린지가 두 곳에 갈라진다. 예전에 고른 값(localStorage)은 더 보지 않는다.
 */
export function getSource() {
  return QBASE_AVAILABLE ? QBASE : LOCAL;
}

/** 예전 호출부 호환 — 출처는 바꿀 수 없다. 지금 출처를 돌려준다 */
export function setSource(_next) {
  return getSource();
}

/** 데이터 공급 함수들이 부르는 갈림길. */
export async function isLocal() {
  return getSource() === LOCAL;
}

/** 동기 렌더에서 쓰는 같은 판정. */
export function isLocalSync() {
  return getSource() === LOCAL;
}

/** 로컬에 가져온 챌린지가 있는지. 전환 버튼을 권할지 판단하는 데 쓴다. 실패는 '없음'. */
export async function hasLocalChallenges() {
  try {
    const res = await axios.get(`${LOCAL_API}/challenges-available`, { timeout: 5000 });
    return !!(res.data && res.data.available);
  } catch (_e) {
    // 이 판정 때문에 화면이 죽으면 안 된다 — 못 물었으면 없는 것으로 친다
    return false;
  }
}

export function qbaseAvailable() {
  return QBASE_AVAILABLE;
}

/** 예전 이름 — 가져오기 뒤 호출하던 것. 이제 캐시가 없어 할 일이 없다. */
export function refresh() { /* no-op: 공급원은 사용자가 고른 값이라 다시 판정할 것이 없다 */ }
